<template>
  <div>
    <div class="cf-card cf-flex cf-flex--align-center cf-flex--row cf-flex--justify-space-between cf-flex--gap-16 cf-mobile-column">
      <h3 class="cf-title cf-title--3">
        Let the host know you're here!
      </h3>

      <button
        class="cf-button cf-button--primary cf-button--md cf-button--mobile-fill"
        type="button"
        :disabled="hasNotifiedStaff || !order.externalOrderId"
        @click="$openModal('iAmHere')"
      >
        I Am Here
      </button>
    </div>

    <!-- I am Here Modal -->
    <modal
      name="iAmHere"
      modal-style="compact"
      :display-header-title="false"
    >
      <div class="cf-flex cf-flex--column cf-padding-bottom-16">
        <div
          v-if="hasNotifiedStaff"
          class="animation-slide-left"
        >
          <div class="cf-flex cf-flex--column cf-flex--gap-16">
            <span class="cf-icon cf-icon--brand-primary cf-icon--large far fa-map-marker-check" />

            <h2 class="cf-title cf-title--2">
              Our host has been notified!
            </h2>

            <p class="cf-subtitle cf-subtitle--2">
              We'll see you soon!
            </p>

            <button
              class="cf-button cf-button--primary cf-button--lg cf-button--fill"
              type="button"
              @click="$closeModal('iAmHere')"
            >
              Okay
            </button>
          </div>
        </div>

        <validated-form
          v-else
          name="iAmHere"
          @valid-submit="notifyStaffGuestIsAtLocation"
        >
          <div class="cf-flex cf-flex--column cf-flex--gap-16">
            <span class="cf-icon cf-icon--brand-primary cf-icon--large far fa-car" />

            <h2 class="cf-title cf-title--2">
              Notify the host!
            </h2>

            <template v-if="checkInOptions">
              <div
                v-for="input in checkInOptions"
                :key="input.property"
              >
                <cf-text-input
                  v-model="details[input.property]"
                  type="text"
                  :label="input.displayName"
                  :name="input.property"
                  :placeholder="input.placeholderText"
                  show-as-optional
                  :validations="{
                    required: input.isRequired
                  }"
                />
              </div>
            </template>

            <div class="cf-flex cf-flex--column cf-flex--gap-10">
              <button
                class="cf-button cf-button--primary cf-button--lg cf-button--fill"
                :class="{
                  'cf-button--loading': isNotifyingStaff
                }"
                type="submit"
              >
                I Am Here
              </button>

              <button
                class="cf-button cf-button--secondary cf-button--lg cf-button--fill"
                type="button"
                @click="$closeModal('iAmHere')"
              >
                Cancel
              </button>
            </div>
          </div>
        </validated-form>
      </div>
    </modal>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';
  import md5 from 'md5';

  import errorHandlers from '@/services/error-handlers';
  import http from '@/services/http';
  import storage from '@/services/storage';

  import CfTextInput from '@/view/components/shared/cf-text-input/index.vue';


  export default {
    name: 'CfGuestArrivedCta',

    components: {
      CfTextInput
    },

    props: {
      autoOpen: {
        type: Boolean,
        default: false
      },
      order: {
        type: Object,
        required: true
      },
      receiptToken: {
        type: String,
        required: true
      }
    },

    data() {
      return {
        details: {},
        hasNotifiedStaff: false,
        isNotifyingStaff: false
      };
    },

    computed: {
      ...mapGetters('merchant', ['merchant']),

      checkInOptions() {
        switch (this.order.pickupLocation) {
          case 'take-out':
            return this.order.store?.insideCheckInOptions
              || this.merchant.options.insideCheckInOptions;

          case 'drive-through':
            return this.order.store?.driveThroughCheckInOptions
              || this.merchant.options.driveThroughCheckInOptions;

          default:
            /**
             * For merchants that use the "I Am Here" feature for "Text to Pay" orders,
             * there won't be any pickup location on the order, so we'll default to
             * using the the curbside check-in options for these orders.
             */
            return this.order.store?.curbsideCheckInOptions
              || this.merchant.options.curbsideCheckInOptions;
        }
      }
    },

    created() {
      this.createdHandler();
    },

    methods: {
      createdHandler() {
        this.conditionallyOpenIamHereModal();
      },

      conditionallyOpenIamHereModal() {
        if (this.autoOpen && this.order.externalOrderId) {
          this.$openModal('iAmHere');
        }
      },

      async notifyStaffGuestIsAtLocation() {
        try {
          this.isNotifyingStaff = true;

          await http.post(`/cardfree-api/v1/receipt/${this.receiptToken}/notify/merchant`, {
            orderHash: md5(this.order.externalOrderId),
            vehicle: {
              guestId: storage.local.get('deviceId'),
              ...this.details
            }
          });

          this.hasNotifiedStaff = true;

          // this.$triggerGoogleTrackingEvent('IAmHere', 'Receipt');

          this.$triggerNewRelicEvent('IAmHere', 'Information', 'I Am Here Receipt');
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.isNotifyingStaff = false;
        }
      }
    }
  };
</script>
